import sodium from 'libsodium-wrappers';
import { SYMMETRIC_PARAMS } from './constants';
import { secureRandom } from './utils';

/**
 * Result of an AEAD encryption operation.
 * The nonce is prepended to the ciphertext so the caller does not need to
 * store it separately — the nonce is not secret, only unique.
 */
export interface EncryptionResult {
  /** 24-byte random nonce (must be stored/transmitted alongside ciphertext) */
  nonce: Uint8Array;
  /** Ciphertext including the 16-byte Poly1305 authentication tag */
  ciphertext: Uint8Array;
}

/**
 * Encrypt plaintext with XChaCha20-Poly1305-IETF AEAD.
 *
 * Security rationale:
 * - **XChaCha20-Poly1305** is an AEAD cipher that provides both
 *   confidentiality (ChaCha20 stream cipher) and integrity/authenticity
 *   (Poly1305 MAC) in a single pass.
 * - The **extended nonce** variant (XChaCha20) uses a 24-byte nonce which
 *   makes random nonce generation safe even for very large message counts
 *   (birthday bound ~ 2^96 vs 2^32 for the 12-byte variant).
 * - The **AAD** (Additional Authenticated Data) is authenticated but NOT
 *   encrypted. Use it to bind ciphertext to context (e.g. vault ID,
 *   user ID) preventing ciphertext from being moved between contexts.
 * - The 16-byte Poly1305 tag is appended to the ciphertext by libsodium.
 *
 * @param plaintext - Data to encrypt (can be empty for header-only AAD)
 * @param key       - 32-byte symmetric key (a DEK or KEK)
 * @param aad       - Additional Authenticated Data (context binding)
 * @returns An {@link EncryptionResult} with nonce and authenticated ciphertext
 */
export function encrypt(
  plaintext: Uint8Array,
  key: Uint8Array,
  aad: Uint8Array = new Uint8Array(0),
): EncryptionResult {
  const nonce = secureRandom(SYMMETRIC_PARAMS.nonceBytes);

  // libsodium's crypto_aead_xchacha20poly1305_ietf_encrypt takes:
  //   message, additional_data, null_secret_nonce, nonce, key
  const ciphertext = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
    plaintext,
    aad,
    null,
    nonce,
    key,
  );

  return { nonce, ciphertext };
}

/**
 * Decrypt an XChaCha20-Poly1305-IETF ciphertext.
 *
 * Security rationale:
 * - The Poly1305 MAC is verified **before** any plaintext is released.
 *   If the ciphertext, nonce, key, or AAD has been tampered with, the
 *   function throws immediately — no partial plaintext is ever returned.
 * - This is the correct "authenticate-then-decrypt" pattern that prevents
 *   padding oracle and similar oracle attacks.
 *
 * @param ciphertext - The authenticated ciphertext (includes the 16-byte tag)
 * @param nonce      - The 24-byte nonce that was used during encryption
 * @param key        - The same 32-byte symmetric key used for encryption
 * @param aad        - The same AAD used during encryption
 * @returns The original plaintext as a Uint8Array
 * @throws {Error} If authentication fails (tampered data or wrong key/AAD)
 */
export function decrypt(
  ciphertext: Uint8Array,
  nonce: Uint8Array,
  key: Uint8Array,
  aad: Uint8Array = new Uint8Array(0),
): Uint8Array {
  const plaintext = sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
    null,
    ciphertext,
    aad,
    nonce,
    key,
  );
  return plaintext;
}

/**
 * Generate a random 32-byte Data Encryption Key (DEK).
 *
 * Security rationale:
 * - DEKs are the per-record symmetric keys in envelope encryption.
 *   They are generated with `randombytes_buf` (CSPRNG) and are
 *   cryptographically indistinguishable from random noise.
 * - Each secret in the vault gets its own DEK so that key rotation
 *   or compromise of one secret does not affect others.
 *
 * @returns A fresh 32-byte Uint8Array suitable for use with {@link encrypt}
 */
export function generateDEK(): Uint8Array {
  return secureRandom(SYMMETRIC_PARAMS.keyBytes);
}
